import React, { useState, useMemo, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import SortableTable from '../SortableTable';
import GIQuestionHeader from './GIQuestionHeader';
import StatTile from '../ui/StatTile';
import { safeParseDate } from '../../utils/dateUtils';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const GINumericQuestion = ({ questionId, questionText, answers, promoters, isCollapsed = false, onToggleCollapse }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [chartMode, setChartMode] = useState('sum'); // 'sum' ou 'average'
  const [rankingMode, setRankingMode] = useState('average'); // 'average' ou 'sum' - moyenne par défaut

  // États pour la modal d'analyse de qualité
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);
  const [isQualityModalOpen, setIsQualityModalOpen] = useState(false);

  // Fonction utilitaire pour sécuriser toLocaleString
  const safeToLocaleString = (value) => {
    if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
      return '0';
    }
    return value.toLocaleString('fr-FR');
  };

  // Préparer les données des réponses numériques
  const numericData = useMemo(() => {
    // Extraire toutes les réponses numériques pour cette question
    const numericAnswers = [];

    if (!answers || !answers.length) return { values: [], tableData: [] };

    // Créer un tableau de valeurs numériques et les données pour le tableau
    answers.forEach(answer => {
      if (answer.Answers[questionId]) {
        const numericValue = parseFloat(answer.Answers[questionId]);
        if (!isNaN(numericValue)) {
          // Obtenir le nom du promoteur selon le format disponible
          let promoterName;
          if (answer.PromoterName) {
            promoterName = answer.PromoterName;
          } else {
            promoterName = 'Promoteur inconnu';
          }

          numericAnswers.push({
            value: numericValue,
            promoter: promoterName,
            date: safeParseDate(answer.AnswerDate),
            interventionPoint: answer.InterventionPointName || 'Non spécifié'
          });
        }
      }
    });

    // Trier les valeurs pour les statistiques
    const sortedValues = numericAnswers.map(a => a.value).sort((a, b) => a - b);

    return {
      values: sortedValues,
      tableData: numericAnswers
    };
  }, [questionId, answers]);

  // Calculer les statistiques
  const stats = useMemo(() => {
    const { values } = numericData;
    if (!values || values.length === 0) {
      return {
        sum: 0,
        average: 0,
        median: 0,
        min: 0,
        max: 0,
        q1: 0,
        q3: 0,
        count: 0
      };
    }

    // Calcul de la moyenne
    const sum = values.reduce((acc, val) => acc + val, 0);
    const average = sum / values.length;

    // Calcul de la médiane
    const middle = Math.floor(values.length / 2);
    const median = values.length % 2 === 0
      ? (values[middle - 1] + values[middle]) / 2
      : values[middle];

    // Utiliser des valeurs par défaut sécurisées
    const minValue = values.length > 0 ? Math.min(...values) : 0;
    const maxValue = values.length > 0 ? Math.max(...values) : 0;

    // Calcul des quartiles
    const sortedValues = [...values].sort((a, b) => a - b);
    const q1Index = Math.floor(sortedValues.length * 0.25);
    const q3Index = Math.floor(sortedValues.length * 0.75);
    const q1 = sortedValues[q1Index];
    const q3 = sortedValues[q3Index];

    return {
      sum: isNaN(sum) ? 0 : parseFloat(sum.toFixed(2)),
      average: isNaN(average) ? 0 : parseFloat(average.toFixed(2)),
      median: isNaN(median) ? 0 : parseFloat(median.toFixed(2)),
      min: isNaN(minValue) || !isFinite(minValue) ? 0 : minValue,
      max: isNaN(maxValue) || !isFinite(maxValue) ? 0 : maxValue,
      q1: isNaN(q1) || !isFinite(q1) ? 0 : q1,
      q3: isNaN(q3) || !isFinite(q3) ? 0 : q3,
      count: values.length
    };
  }, [numericData]);

  // Calculer les données d'évolution par jour
  const evolutionData = useMemo(() => {
    const { tableData } = numericData;
    if (!tableData || tableData.length === 0) {
      return {
        chartData: null,
        dailyStats: []
      };
    }

    // Grouper les données par jour
    const dailyData = {};
    tableData.forEach(item => {
      if (!item.date) return; // Ignorer les entrées avec date invalide
      const dayKey = item.date.toISOString().split('T')[0]; // Format YYYY-MM-DD
      if (!dailyData[dayKey]) {
        dailyData[dayKey] = {
          date: dayKey,
          values: [],
          sum: 0,
          count: 0
        };
      }
      dailyData[dayKey].values.push(item.value);
      dailyData[dayKey].sum += item.value;
      dailyData[dayKey].count += 1;
    });

    // Trier par date et préparer les données pour le graphique
    const sortedDays = Object.keys(dailyData).sort();
    const dailyStats = sortedDays.map(dayKey => ({
      ...dailyData[dayKey],
      formattedDate: new Date(dayKey).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      }),
      average: dailyData[dayKey].sum / dailyData[dayKey].count
    }));

    // Préparer les données pour Chart.js
    const chartData = {
      labels: dailyStats.map(day => day.formattedDate),
      datasets: [
        {
          label: 'Somme par jour',
          data: dailyStats.map(day => day.sum),
          backgroundColor: 'rgba(59, 130, 246, 0.8)', // blue-500 avec transparence
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1,
          borderRadius: 4,
        }
      ]
    };

    return {
      chartData,
      dailyStats
    };
  }, [numericData]);

  // Préparer les données du graphique selon le mode sélectionné
  const chartDisplayData = useMemo(() => {
    if (!evolutionData.chartData) return null;

    const { dailyStats } = evolutionData;

    return {
      labels: dailyStats.map(day => day.formattedDate),
      datasets: [
        {
          label: chartMode === 'sum' ? 'Somme par jour' : 'Moyenne par jour',
          data: dailyStats.map(day => chartMode === 'sum' ? day.sum : day.average),
          backgroundColor: chartMode === 'sum'
            ? 'rgba(59, 130, 246, 0.8)' // blue pour somme
            : 'rgba(16, 185, 129, 0.8)', // emerald pour moyenne
          borderColor: chartMode === 'sum'
            ? 'rgba(59, 130, 246, 1)'
            : 'rgba(16, 185, 129, 1)',
          borderWidth: 1,
          borderRadius: 4,
        }
      ]
    };
  }, [evolutionData, chartMode]);

  // Calculer les rankings par promoteur et point d'intervention
  const rankingsData = useMemo(() => {
    const { tableData } = numericData;
    if (!tableData || tableData.length === 0) {
      return {
        promoterRankings: [],
        interventionPointRankings: []
      };
    }

    // Grouper par promoteur
    const promoterData = {};
    tableData.forEach(item => {
      if (!promoterData[item.promoter]) {
        promoterData[item.promoter] = {
          promoter: item.promoter,
          values: [],
          sum: 0,
          count: 0
        };
      }
      promoterData[item.promoter].values.push(item.value);
      promoterData[item.promoter].sum += item.value;
      promoterData[item.promoter].count += 1;
    });

    // Calculer les statistiques par promoteur et trier
    const promoterRankings = Object.values(promoterData)
      .map(data => ({
        promoter: data.promoter,
        sum: parseFloat(data.sum.toFixed(2)),
        average: parseFloat((data.sum / data.count).toFixed(2)),
        count: data.count,
        min: Math.min(...data.values),
        max: Math.max(...data.values)
      }))
      .sort((a, b) => rankingMode === 'average' ? b.average - a.average : b.sum - a.sum); // Tri selon le mode sélectionné

    // Grouper par point d'intervention (récupérer depuis les réponses originales)
    const interventionPointData = {};
    if (answers && answers.length > 0) {
      answers.forEach(answer => {
        if (answer.Answers[questionId] && answer.InterventionPointName) {
          const numericValue = parseFloat(answer.Answers[questionId]);
          if (!isNaN(numericValue)) {
            const pointName = answer.InterventionPointName;
            if (!interventionPointData[pointName]) {
              interventionPointData[pointName] = {
                interventionPoint: pointName,
                values: [],
                sum: 0,
                count: 0
              };
            }
            interventionPointData[pointName].values.push(numericValue);
            interventionPointData[pointName].sum += numericValue;
            interventionPointData[pointName].count += 1;
          }
        }
      });
    }

    // Calculer les statistiques par point d'intervention et trier
    const interventionPointRankings = Object.values(interventionPointData)
      .map(data => ({
        interventionPoint: data.interventionPoint,
        sum: parseFloat(data.sum.toFixed(2)),
        average: parseFloat((data.sum / data.count).toFixed(2)),
        count: data.count,
        min: Math.min(...data.values),
        max: Math.max(...data.values)
      }))
      .sort((a, b) => rankingMode === 'average' ? b.average - a.average : b.sum - a.sum); // Tri selon le mode sélectionné

    return {
      promoterRankings,
      interventionPointRankings
    };
  }, [numericData, answers, questionId, rankingMode]);

  // Calculer les données de distribution (histogramme)
  const distributionData = useMemo(() => {
    const { values } = numericData;
    if (!values || values.length === 0) {
      return {
        chartData: null,
        bins: [],
        totalCount: 0
      };
    }

    // Déterminer le nombre de bins optimal (règle de Sturges ou racine carrée)
    const binCount = Math.max(5, Math.min(20, Math.ceil(Math.sqrt(values.length))));

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;

    // Si toutes les valeurs sont identiques, créer un seul bin
    if (range === 0) {
      return {
        chartData: {
          labels: [safeToLocaleString(min)],
          datasets: [{
            label: 'Fréquence',
            data: [values.length],
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 1,
            borderRadius: 4,
          }]
        },
        bins: [{
          range: `${safeToLocaleString(min)}`,
          count: values.length,
          percentage: 100
        }],
        totalCount: values.length
      };
    }

    const binWidth = range / binCount;
    const bins = [];

    // Créer les bins
    for (let i = 0; i < binCount; i++) {
      const binStart = min + (i * binWidth);
      const binEnd = i === binCount - 1 ? max : min + ((i + 1) * binWidth);

      // Compter les valeurs dans ce bin
      const count = values.filter(value => {
        if (i === binCount - 1) {
          // Dernier bin : inclure la valeur max
          return value >= binStart && value <= binEnd;
        } else {
          // Autres bins : exclure la borne supérieure
          return value >= binStart && value < binEnd;
        }
      }).length;

      if (count > 0 || i === 0 || i === binCount - 1) { // Toujours inclure le premier et dernier bin
        bins.push({
          range: `${safeToLocaleString(parseFloat(binStart.toFixed(2)))} - ${safeToLocaleString(parseFloat(binEnd.toFixed(2)))}`,
          count: count,
          percentage: parseFloat(((count / values.length) * 100).toFixed(1)),
          binStart: binStart,
          binEnd: binEnd
        });
      }
    }

    // Préparer les données pour Chart.js
    const chartData = {
      labels: bins.map(bin => bin.range),
      datasets: [{
        label: 'Fréquence',
        data: bins.map(bin => bin.count),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
        borderRadius: 4,
      }]
    };

    return {
      chartData,
      bins,
      totalCount: values.length
    };
  }, [numericData]);

  // Fonction d'analyse statistique pour détecter les valeurs aberrantes
  const analyzeDataQuality = useMemo(() => {
    const { values } = numericData;
    if (!values || values.length < 3) {
      // Pas assez de données pour une analyse statistique fiable
      return () => ({ status: 'OK', reasons: [] });
    }

    // Calculs statistiques de base
    const sortedValues = [...values].sort((a, b) => a - b);
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const median = sortedValues.length % 2 === 0
      ? (sortedValues[Math.floor(sortedValues.length / 2) - 1] + sortedValues[Math.floor(sortedValues.length / 2)]) / 2
      : sortedValues[Math.floor(sortedValues.length / 2)];

    // Calcul de l'écart-type
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);

    // Calcul des quartiles pour la méthode IQR
    const q1 = stats.q1;
    const q3 = stats.q3;
    const iqr = q3 - q1;

    // Calcul de la MAD (Median Absolute Deviation) pour le Modified Z-Score
    const mad = sortedValues.map(val => Math.abs(val - median))
      .sort((a, b) => a - b)[Math.floor(sortedValues.length / 2)];

    // Fonction d'analyse pour une valeur donnée
    return (value) => {
      const reasons = [];
      let status = 'OK';

      // 1. Méthode IQR - Détection des outliers classiques
      const iqrLowerBound = q1 - 1.5 * iqr;
      const iqrUpperBound = q3 + 1.5 * iqr;
      const iqrWarningLower = q1 - iqr;
      const iqrWarningUpper = q3 + iqr;

      if (value < iqrLowerBound || value > iqrUpperBound) {
        status = 'NOK';
        reasons.push(`Outlier IQR: ${value < iqrLowerBound ? 'très inférieur' : 'très supérieur'} aux autres valeurs`);
      } else if (value < iqrWarningLower || value > iqrWarningUpper) {
        if (status !== 'NOK') status = 'WARNING';
        reasons.push(`Valeur atypique: ${value < iqrWarningLower ? 'inférieure' : 'supérieure'} à la normale`);
      }

      // 2. Z-Score - Écart par rapport à la moyenne
      if (stdDev > 0) {
        const zScore = Math.abs((value - mean) / stdDev);
        if (zScore > 3) {
          status = 'NOK';
          reasons.push(`Z-Score élevé (${zScore.toFixed(1)}): très éloigné de la moyenne`);
        } else if (zScore > 2 && status !== 'NOK') {
          if (status !== 'NOK') status = 'WARNING';
          reasons.push(`Z-Score modéré (${zScore.toFixed(1)}): éloigné de la moyenne`);
        }
      }

      // 3. Modified Z-Score - Plus robuste
      if (mad > 0) {
        const modifiedZScore = Math.abs(0.6745 * (value - median) / mad);
        if (modifiedZScore > 3.5) {
          status = 'NOK';
          reasons.push(`M-Z-Score élevé (${modifiedZScore.toFixed(1)}): très éloigné de la médiane`);
        } else if (modifiedZScore > 2.5 && status !== 'NOK') {
          if (status !== 'NOK') status = 'WARNING';
          reasons.push(`M-Z-Score modéré (${modifiedZScore.toFixed(1)}): éloigné de la médiane`);
        }
      }

      // 4. Analyses contextuelles
      // Valeurs exactement 0 (potentiellement suspectes selon le contexte)
      if (value === 0 && values.filter(v => v === 0).length < values.length * 0.1) {
        if (status !== 'NOK') status = 'WARNING';
        reasons.push('Valeur zéro: potentiellement non renseignée');
      }

      // Valeurs négatives (selon le contexte métier)
      if (value < 0) {
        if (status !== 'NOK') status = 'WARNING';
        reasons.push('Valeur négative: vérifier la cohérence métier');
      }

      // Valeurs rondes suspectes (multiples de 10, 100, etc.) si elles sont sur-représentées
      const roundValues = values.filter(v => v % 10 === 0 || v % 100 === 0 || v % 1000 === 0);
      if ((value % 10 === 0 || value % 100 === 0 || value % 1000 === 0) &&
          roundValues.length > values.length * 0.3 && values.length > 10) {
        if (status !== 'NOK' && status !== 'WARNING') status = 'WARNING';
        reasons.push('Valeur ronde: potentiellement approximative');
      }

      // Valeurs dupliquées en masse (même valeur répétée de façon suspecte)
      // uniquement si mean > 100
      const duplicateCount = values.filter(v => v === value).length;
      if (duplicateCount > Math.max(3, values.length * 0.2) && values.length > 10 && mean > 100) {
        if (status !== 'NOK') status = 'WARNING';
        reasons.push(`Valeur dupliquée: répétée ${duplicateCount} fois`);
      }

      return { status, reasons };
    };
  }, [numericData]);

  // Enrichir les données du tableau avec l'analyse de qualité
  const enrichedTableData = useMemo(() => {
    return numericData.tableData.map(item => {
      const qualityAnalysis = analyzeDataQuality(item.value);
      return {
        ...item,
        qualityStatus: qualityAnalysis.status,
        qualityReasons: qualityAnalysis.reasons
      };
    });
  }, [numericData.tableData, analyzeDataQuality]);

  // Définir les colonnes pour le tableau SortableTable
  const columns = [
      {
      key: 'date',
      label: 'Date',
      render: (item) => item.date.toLocaleDateString('fr-FR')
    },
    {
      key: 'promoter',
      label: 'Promoteur',
    },
    {
      key: 'value',
      label: 'Valeur',
      render: (item) => safeToLocaleString(item.value)
    },
    {
      key: 'qualityStatus',
      label: 'Vérification',
      render: (item) => (
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            item.qualityStatus === 'OK'
              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
              : item.qualityStatus === 'WARNING'
              ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
              : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
          }`}>
            {item.qualityStatus === 'OK' ? '✅ OK' : item.qualityStatus === 'WARNING' ? '⚠️ WARNING' : '❌ NOK'}
          </span>
          {item.qualityReasons.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenQualityModal(item);
              }}
              className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              title="Voir l'analyse détaillée"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </button>
          )}
        </div>
      ),
      rawValue: (item) => item.qualityStatus // Pour le tri
    },
    {
      key: 'interventionPoint',
      label: 'Point d\'intervention',
    }
  ];

  // Fonctions pour gérer la modal d'analyse de qualité
  const handleOpenQualityModal = (dataPoint) => {
    setSelectedDataPoint(dataPoint);
    setIsQualityModalOpen(true);
  };

  const handleCloseQualityModal = () => {
    setIsQualityModalOpen(false);
    setSelectedDataPoint(null);
  };

  // Gérer la fermeture de la modal avec la touche ECHAP
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isQualityModalOpen) {
        handleCloseQualityModal();
      }
    };

    if (isQualityModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Désactiver le scroll de la page en arrière-plan
      document.body.style.overflow = 'hidden';
    } else {
      // Réactiver le scroll de la page
      document.body.style.overflow = 'unset';
    }

    // Cleanup au démontage du composant ou changement d'état
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isQualityModalOpen]);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <GIQuestionHeader
        questionId={questionId}
        questionText={questionText}
        questionType="Numérique"
        badgeLabel="Num"
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
      />

      {!isCollapsed && (
        <>
      {/* Tabs */}
      <div className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <nav className="flex">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'summary'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 hidden sm:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Résumé
          </button>
          <button
            onClick={() => setActiveTab('evolution')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'evolution'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 hidden sm:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
            Évolution
          </button>
          <button
            onClick={() => setActiveTab('distribution')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'distribution'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 hidden sm:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Distribution
          </button>
          <button
            onClick={() => setActiveTab('rankings')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'rankings'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 hidden sm:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l2.4 7.4h7.6l-6.2 4.5 2.4 7.4-6.2-4.5-6.2 4.5 2.4-7.4-6.2-4.5h7.6L12 2z" />
            </svg>
            Rankings
          </button>
          <button
            onClick={() => setActiveTab('detail')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'detail'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 hidden sm:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Détail
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'summary' ? (
          <div className="space-y-6">
            {/* Statistiques principales */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatTile
                value={safeToLocaleString(stats.sum)}
                label="Somme"
                color="blue"
                size="s"
                subValue={`${safeToLocaleString(stats.count)} réponses`}
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                }
              />
              <StatTile
                value={safeToLocaleString(stats.average)}
                label="Moyenne"
                color="blue"
                size="s"
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                }
              />
              <StatTile
                value={safeToLocaleString(stats.median)}
                label="Médiane"
                color="indigo"
                size="s"
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                }
              />
              <StatTile
                value={safeToLocaleString(stats.min)}
                label="Minimum"
                color="green"
                size="s"
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                }
              />
              <StatTile
                value={safeToLocaleString(stats.max)}
                label="Maximum"
                color="red"
                size="s"
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                }
              />
            </div>

            {/* Résumé de la qualité des données */}
            {enrichedTableData.length > 0 && (() => {
              const qualityStats = {
                ok: enrichedTableData.filter(item => item.qualityStatus === 'OK').length,
                warning: enrichedTableData.filter(item => item.qualityStatus === 'WARNING').length,
                nok: enrichedTableData.filter(item => item.qualityStatus === 'NOK').length
              };
              const total = qualityStats.ok + qualityStats.warning + qualityStats.nok;

              return (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Analyse de la qualité des données
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-lg font-semibold text-green-800 dark:text-green-200">
                            {qualityStats.ok}
                          </div>
                          <div className="text-sm text-green-600 dark:text-green-400">
                            Données fiables
                          </div>
                        </div>
                        <div className="text-green-500 text-sm font-medium">
                          {total > 0 ? `${((qualityStats.ok / total) * 100).toFixed(1)}%` : '0%'}
                        </div>
                      </div>
                    </div>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">
                            {qualityStats.warning}
                          </div>
                          <div className="text-sm text-yellow-600 dark:text-yellow-400">
                            Données à vérifier
                          </div>
                        </div>
                        <div className="text-yellow-500 text-sm font-medium">
                          {total > 0 ? `${((qualityStats.warning / total) * 100).toFixed(1)}%` : '0%'}
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-lg font-semibold text-red-800 dark:text-red-200">
                            {qualityStats.nok}
                          </div>
                          <div className="text-sm text-red-600 dark:text-red-400">
                            Données suspectes
                          </div>
                        </div>
                        <div className="text-red-500 text-sm font-medium">
                          {total > 0 ? `${((qualityStats.nok / total) * 100).toFixed(1)}%` : '0%'}
                        </div>
                      </div>
                    </div>
                  </div>
                  {(qualityStats.warning > 0 || qualityStats.nok > 0) && (
                    <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-800 dark:text-blue-200">
                      💡 Consultez l'onglet "Détail" pour voir les raisons spécifiques de chaque alerte
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        ) : activeTab === 'detail' ? (
          <div className="h-72">
            <SortableTable
              data={enrichedTableData}
              columns={columns}
              emptyMessage="Aucune réponse numérique pour cette question"
              exportable={true}
              exportFilename={`question-${questionId}-detail.csv`}
            />
          </div>
        ) : activeTab === 'evolution' ? (
          // Tab évolution
          <div className="space-y-4">
            {chartDisplayData ? (
              <>
                {/* Statistiques d'évolution */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Évolution sur <span className="font-semibold text-gray-800 dark:text-gray-200">{evolutionData.dailyStats.length}</span> jour{evolutionData.dailyStats.length > 1 ? 's' : ''}
                    <span className="ml-3">
                      Somme totale: <span className="font-semibold text-gray-800 dark:text-gray-200">{safeToLocaleString(stats.sum)}</span>
                    </span>
                  </div>
                </div>

                {/* Graphique avec sélecteur de mode */}
                <div className="relative">
                  {/* Sélecteur de mode en haut à droite */}
                  <div className="absolute top-2 right-2 z-10 flex bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 overflow-hidden">
                    <button
                      onClick={() => setChartMode('sum')}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        chartMode === 'sum'
                          ? 'bg-blue-500 text-white'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      Somme
                    </button>
                    <button
                      onClick={() => setChartMode('average')}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        chartMode === 'average'
                          ? 'bg-emerald-500 text-white'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      Moyenne
                    </button>
                  </div>

                  {/* Graphique */}
                  <div className="h-64 w-full">
                    <Bar
                      data={chartDisplayData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: `Évolution des ${chartMode === 'sum' ? 'sommes' : 'moyennes'} par jour`,
                            color: 'rgb(75, 85, 99)' // gray-600
                          },
                          legend: {
                            display: false
                          },
                          tooltip: {
                            callbacks: {
                              label: function(context) {
                                const dayStats = evolutionData.dailyStats[context.dataIndex];
                                return [
                                  `Somme: ${safeToLocaleString(dayStats.sum)}`,
                                  `Moyenne: ${safeToLocaleString(dayStats.average)}`,
                                  `Réponses: ${dayStats.count}`
                                ];
                              }
                            }
                          }
                        },
                        scales: {
                          x: {
                            title: {
                              display: true,
                              text: 'Date',
                              color: 'rgb(75, 85, 99)'
                            },
                            ticks: {
                              color: 'rgb(107, 114, 128)' // gray-500
                            }
                          },
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: chartMode === 'sum' ? 'Somme des valeurs' : 'Moyenne des valeurs',
                              color: 'rgb(75, 85, 99)'
                            },
                            ticks: {
                              color: 'rgb(107, 114, 128)'
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Tableau détaillé par jour */}
                <div className="mt-4">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Détail par jour</h5>
                  <div className="h-64">
                    <SortableTable
                      data={evolutionData.dailyStats.map(day => ({
                        date: day.date,
                        formattedDate: new Date(day.date).toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }).replace(/^\w/, c => c.toUpperCase()),
                        sum: day.sum,
                        average: day.average,
                        count: day.count
                      }))}
                      columns={[
                        {
                          key: 'formattedDate',
                          label: 'Date',
                          render: (item) => item.formattedDate
                        },
                        {
                          key: 'sum',
                          label: 'Somme',
                          render: (item) => safeToLocaleString(item.sum)
                        },
                        {
                          key: 'average',
                          label: 'Moyenne',
                          render: (item) => safeToLocaleString(item.average)
                        },
                        {
                          key: 'count',
                          label: 'Réponses',
                          render: (item) => item.count
                        }
                      ]}
                      emptyMessage="Aucune donnée d'évolution disponible"
                      countLabel={`${evolutionData.dailyStats.length} jour${evolutionData.dailyStats.length > 1 ? 's' : ''}`}
                      exportable={true}
                      exportFilename={`question-${questionId}-evolution.csv`}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Aucune donnée d'évolution disponible
              </div>
            )}
          </div>
        ) : activeTab === 'distribution' ? (
          // Tab distribution
          <div className="space-y-4">
            {distributionData.chartData ? (
              <>
                {/* Informations sur la distribution */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Distribution sur <span className="font-semibold text-gray-800 dark:text-gray-200">{distributionData.totalCount}</span> valeur{distributionData.totalCount > 1 ? 's' : ''}
                    <span className="ml-3">
                      Étendue: <span className="font-semibold text-gray-800 dark:text-gray-200">{safeToLocaleString(stats.min)} - {safeToLocaleString(stats.max)}</span>
                    </span>
                    <span className="ml-3">
                      {distributionData.bins.length} intervalle{distributionData.bins.length > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {/* Graphique histogramme */}
                <div className="h-64 w-full">
                  <Bar
                    data={distributionData.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Distribution des valeurs (Histogramme)',
                          color: 'rgb(75, 85, 99)' // gray-600
                        },
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const bin = distributionData.bins[context.dataIndex];
                              return [
                                `Fréquence: ${bin.count}`,
                                `Pourcentage: ${bin.percentage}%`,
                                `Intervalle: ${bin.range}`
                              ];
                            }
                          }
                        }
                      },
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text: 'Intervalles de valeurs',
                            color: 'rgb(75, 85, 99)'
                          },
                          ticks: {
                            color: 'rgb(107, 114, 128)', // gray-500
                            maxRotation: 45,
                            minRotation: 0
                          }
                        },
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Fréquence (nombre de valeurs)',
                            color: 'rgb(75, 85, 99)'
                          },
                          ticks: {
                            color: 'rgb(107, 114, 128)',
                            stepSize: 1
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Tableau détaillé de la distribution */}
                <div className="mt-4">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Détail de la distribution</h5>
                  <div className="h-64">
                    <SortableTable
                      data={distributionData.bins.map((bin, index) => ({
                        interval: bin.range,
                        count: bin.count,
                        percentage: bin.percentage,
                        binStart: bin.binStart,
                        binEnd: bin.binEnd,
                        index: index + 1
                      }))}
                      columns={[
                        {
                          key: 'index',
                          label: '#',
                          render: (item) => item.index
                        },
                        {
                          key: 'interval',
                          label: 'Intervalle',
                          render: (item) => item.interval
                        },
                        {
                          key: 'count',
                          label: 'Fréquence',
                          render: (item) => (
                            <span className="font-semibold text-blue-600 dark:text-blue-400">
                              {item.count}
                            </span>
                          )
                        },
                        {
                          key: 'percentage',
                          label: 'Pourcentage',
                          render: (item) => (
                            <div className="flex items-center gap-2">
                              <span>{item.percentage}%</span>
                              <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${item.percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          )
                        }
                      ]}
                      emptyMessage="Aucune donnée de distribution disponible"
                      countLabel={`${distributionData.bins.length} intervalle${distributionData.bins.length > 1 ? 's' : ''}`}
                      exportable={true}
                      exportFilename={`question-${questionId}-distribution.csv`}
                      initialSortBy={{ key: 'index', direction: 'ascending' }}
                    />
                  </div>
                </div>

                {/* Indicateurs statistiques avec marqueurs visuels */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Indicateurs de distribution
                  </h5>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div className="bg-white dark:bg-gray-700 rounded p-3">
                      <div className="text-gray-500 dark:text-gray-400">Moyenne</div>
                      <div className="font-semibold text-blue-600 dark:text-blue-400">
                        {safeToLocaleString(stats.average)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-700 rounded p-3">
                      <div className="text-gray-500 dark:text-gray-400">Médiane</div>
                      <div className="font-semibold text-indigo-600 dark:text-indigo-400">
                        {safeToLocaleString(stats.median)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-700 rounded p-3">
                      <div className="text-gray-500 dark:text-gray-400">Écart-type</div>
                      <div className="font-semibold text-gray-600 dark:text-gray-400">
                        {(() => {
                          const { values } = numericData;
                          if (values.length === 0) return '0';
                          const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
                          const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
                          const stdDev = Math.sqrt(variance);
                          return safeToLocaleString(parseFloat(stdDev.toFixed(2)));
                        })()}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-700 rounded p-3">
                      <div className="text-gray-500 dark:text-gray-400">Q1 (25%)</div>
                      <div className="font-semibold text-green-600 dark:text-green-400">
                        {safeToLocaleString(stats.q1)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-700 rounded p-3">
                      <div className="text-gray-500 dark:text-gray-400">Q3 (75%)</div>
                      <div className="font-semibold text-orange-600 dark:text-orange-400">
                        {safeToLocaleString(stats.q3)}
                      </div>
                    </div>
                    <div className="bg-white dark:bg-gray-700 rounded p-3">
                      <div className="text-gray-500 dark:text-gray-400">Étendue</div>
                      <div className="font-semibold text-purple-600 dark:text-purple-400">
                        {safeToLocaleString(stats.max - stats.min)}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Aucune donnée de distribution disponible
              </div>
            )}
          </div>
        ) : (
          // Tab rankings
          <div className="space-y-4">
            {/* Sélecteur de mode de ranking */}
            <div className="flex justify-between items-center">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex-1 mr-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{rankingsData.promoterRankings.length}</span> promoteur{rankingsData.promoterRankings.length > 1 ? 's' : ''} classé{rankingsData.promoterRankings.length > 1 ? 's' : ''}
                  <span className="ml-3">
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{rankingsData.interventionPointRankings.length}</span> point{rankingsData.interventionPointRankings.length > 1 ? 's' : ''} d'intervention classé{rankingsData.interventionPointRankings.length > 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Sélecteur de mode de ranking */}
              <div className="flex bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 overflow-hidden">
                <button
                  onClick={() => setRankingMode('average')}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    rankingMode === 'average'
                      ? 'bg-green-500 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Par moyenne
                </button>
                <button
                  onClick={() => setRankingMode('sum')}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    rankingMode === 'sum'
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Par somme
                </button>
              </div>
            </div>

            {/* Tableaux côte à côte */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Ranking des promoteurs */}
              <div>
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  🏆 Ranking des promoteurs
                </h5>
                <div className="h-72">
                  <SortableTable
                    data={rankingsData.promoterRankings.map((item, index) => ({
                      rank: index + 1,
                      promoter: item.promoter,
                      sum: item.sum,
                      average: item.average,
                      count: item.count,
                      min: item.min,
                      max: item.max
                    }))}
                    columns={[
                      {
                        key: 'rank',
                        label: 'Rang',
                        render: (item) => (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.rank === 1 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                            item.rank === 2 ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                            item.rank === 3 ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' :
                            'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                          }`}>
                            {item.rank === 1 ? '🥇' : item.rank === 2 ? '🥈' : item.rank === 3 ? '🥉' : item.rank}
                          </span>
                        )
                      },
                      {
                        key: 'promoter',
                        label: 'Promoteur',
                        render: (item) => item.promoter
                      },
                      {
                        key: 'sum',
                        label: 'Somme',
                        render: (item) => (
                          <span className={rankingMode === 'sum' ? 'font-bold text-blue-600 dark:text-blue-400' : ''}>
                            {safeToLocaleString(item.sum)}
                          </span>
                        )
                      },
                      {
                        key: 'average',
                        label: 'Moyenne',
                        render: (item) => (
                          <span className={rankingMode === 'average' ? 'font-bold text-green-600 dark:text-green-400' : ''}>
                            {safeToLocaleString(item.average)}
                          </span>
                        )
                      },
                      {
                        key: 'count',
                        label: 'Réponses',
                        render: (item) => item.count
                      }
                    ]}
                    emptyMessage="Aucun promoteur trouvé"
                    countLabel={`${rankingsData.promoterRankings.length} promoteur${rankingsData.promoterRankings.length > 1 ? 's' : ''}`}
                    exportable={true}
                    exportFilename={`question-${questionId}-ranking-promoteurs.csv`}
                  />
                </div>
              </div>

              {/* Ranking des points d'intervention */}
              <div>
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  📍 Ranking des points d'intervention
                </h5>
                <div className="h-72">
                  <SortableTable
                    data={rankingsData.interventionPointRankings.map((item, index) => ({
                      rank: index + 1,
                      interventionPoint: item.interventionPoint,
                      sum: item.sum,
                      average: item.average,
                      count: item.count,
                      min: item.min,
                      max: item.max
                    }))}
                    columns={[
                      {
                        key: 'rank',
                        label: 'Rang',
                        render: (item) => (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.rank === 1 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                            item.rank === 2 ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                            item.rank === 3 ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' :
                            'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                          }`}>
                            {item.rank === 1 ? '🥇' : item.rank === 2 ? '🥈' : item.rank === 3 ? '🥉' : item.rank}
                          </span>
                        )
                      },
                      {
                        key: 'interventionPoint',
                        label: 'Point d\'intervention',
                        render: (item) => item.interventionPoint
                      },
                      {
                        key: 'sum',
                        label: 'Somme',
                        render: (item) => (
                          <span className={rankingMode === 'sum' ? 'font-bold text-blue-600 dark:text-blue-400' : ''}>
                            {safeToLocaleString(item.sum)}
                          </span>
                        )
                      },
                      {
                        key: 'average',
                        label: 'Moyenne',
                        render: (item) => (
                          <span className={rankingMode === 'average' ? 'font-bold text-green-600 dark:text-green-400' : ''}>
                            {safeToLocaleString(item.average)}
                          </span>
                        )
                      },
                      {
                        key: 'count',
                        label: 'Réponses',
                        render: (item) => item.count
                      }
                    ]}
                    emptyMessage="Aucun point d'intervention trouvé"
                    countLabel={`${rankingsData.interventionPointRankings.length} point${rankingsData.interventionPointRankings.length > 1 ? 's' : ''} d'intervention`}
                    exportable={true}
                    exportFilename={`question-${questionId}-ranking-points.csv`}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {/* Modal d'analyse de qualité des données */}
      {isQualityModalOpen && selectedDataPoint && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseQualityModal}
            ></div>

            {/* Centrage vertical */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            {/* Contenu de la modal */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      Analyse de qualité des données
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Valeur: <span className="font-semibold">{safeToLocaleString(selectedDataPoint.value)}</span> •
                      Promoteur: <span className="font-semibold">{selectedDataPoint.promoter}</span> •
                      Date: <span className="font-semibold">{selectedDataPoint.date.toLocaleDateString('fr-FR')}</span>
                    </p>
                  </div>
                  <button
                    onClick={handleCloseQualityModal}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Graphique de répartition */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Répartition des valeurs
                    </h4>

                    {/* Histogramme simple avec positionnement */}
                    <div className="space-y-4">
                      {/* Statistiques de base */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="bg-white dark:bg-gray-800 rounded p-3">
                          <div className="text-gray-500 dark:text-gray-400">Moyenne</div>
                          <div className="font-semibold text-blue-600 dark:text-blue-400">
                            {safeToLocaleString(stats.average)}
                          </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded p-3">
                          <div className="text-gray-500 dark:text-gray-400">Médiane</div>
                          <div className="font-semibold text-indigo-600 dark:text-indigo-400">
                            {safeToLocaleString(stats.median)}
                          </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded p-3">
                          <div className="text-gray-500 dark:text-gray-400">Min - Max</div>
                          <div className="font-semibold text-gray-600 dark:text-gray-400">
                            {safeToLocaleString(stats.min)} - {safeToLocaleString(stats.max)}
                          </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded p-3">
                          <div className="text-gray-500 dark:text-gray-400">Écart-type</div>
                          <div className="font-semibold text-gray-600 dark:text-gray-400">
                            {(() => {
                              const { values } = numericData;
                              if (values.length === 0) return '0';
                              const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
                              const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
                              const stdDev = Math.sqrt(variance);
                              return safeToLocaleString(parseFloat(stdDev.toFixed(2)));
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Visualisation de la position de la valeur */}
                      <div className="bg-white dark:bg-gray-800 rounded p-4">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          Position de la valeur dans la distribution
                        </div>

                        {/* Ligne de distribution */}
                        <div className="relative h-12 bg-gradient-to-r from-green-200 via-yellow-200 to-red-200 dark:from-green-800 dark:via-yellow-800 dark:to-red-800 rounded-lg overflow-hidden">
                          {/* Marqueurs des quartiles */}
                          <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gray-400 dark:bg-gray-600"></div>
                          <div className="absolute top-0 left-1/2 w-0.5 h-full bg-gray-600 dark:bg-gray-400"></div>
                          <div className="absolute top-0 left-3/4 w-0.5 h-full bg-gray-400 dark:bg-gray-600"></div>

                          {/* Position de la valeur sélectionnée */}
                          {(() => {
                            const { values } = numericData;
                            if (values.length === 0) return null;

                            const sortedValues = [...values].sort((a, b) => a - b);
                            const position = sortedValues.indexOf(selectedDataPoint.value);
                            const percentile = ((position + 1) / sortedValues.length) * 100;

                            return (
                              <div
                                className="absolute top-0 w-1 h-full bg-blue-600 dark:bg-blue-400 transform -translate-x-1/2"
                                style={{ left: `${percentile}%` }}
                                title={`${percentile.toFixed(1)}e percentile`}
                              >
                                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-600 dark:bg-blue-400 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                  {safeToLocaleString(selectedDataPoint.value)}
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* Légende */}
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
                          <span>Min ({safeToLocaleString(stats.min)})</span>
                          <span>Q1 ({safeToLocaleString(stats.q1)})</span>
                          <span>Médiane ({safeToLocaleString(stats.median)})</span>
                          <span>Q3 ({safeToLocaleString(stats.q3)})</span>
                          <span>Max ({safeToLocaleString(stats.max)})</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Analyse détaillée */}
                  <div className="space-y-4">
                    {/* Statut global */}
                    <div className={`rounded-lg p-4 border-l-4 ${
                      selectedDataPoint.qualityStatus === 'OK'
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-400'
                        : selectedDataPoint.qualityStatus === 'WARNING'
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-400'
                        : 'bg-red-50 dark:bg-red-900/20 border-red-400'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-lg ${
                          selectedDataPoint.qualityStatus === 'OK' ? 'text-green-600 dark:text-green-400' :
                          selectedDataPoint.qualityStatus === 'WARNING' ? 'text-yellow-600 dark:text-yellow-400' :
                          'text-red-600 dark:text-red-400'
                        }`}>
                          {selectedDataPoint.qualityStatus === 'OK' ? '✅' : selectedDataPoint.qualityStatus === 'WARNING' ? '⚠️' : '❌'}
                        </span>
                        <span className={`font-semibold ${
                          selectedDataPoint.qualityStatus === 'OK' ? 'text-green-800 dark:text-green-200' :
                          selectedDataPoint.qualityStatus === 'WARNING' ? 'text-yellow-800 dark:text-yellow-200' :
                          'text-red-800 dark:text-red-200'
                        }`}>
                          {selectedDataPoint.qualityStatus === 'OK' ? 'Donnée fiable' :
                           selectedDataPoint.qualityStatus === 'WARNING' ? 'Donnée à vérifier' :
                           'Donnée suspecte'}
                        </span>
                      </div>
                      <p className={`text-sm ${
                        selectedDataPoint.qualityStatus === 'OK' ? 'text-green-700 dark:text-green-300' :
                        selectedDataPoint.qualityStatus === 'WARNING' ? 'text-yellow-700 dark:text-yellow-300' :
                        'text-red-700 dark:text-red-300'
                      }`}>
                        {selectedDataPoint.qualityStatus === 'OK'
                          ? 'Cette valeur est dans les normes statistiques attendues.'
                          : selectedDataPoint.qualityStatus === 'WARNING'
                          ? 'Cette valeur présente des caractéristiques inhabituelles qui méritent une vérification.'
                          : 'Cette valeur présente des anomalies statistiques significatives qui nécessitent une attention particulière.'}
                      </p>
                    </div>

                    {/* Détails des analyses */}
                    {selectedDataPoint.qualityReasons.length > 0 && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                          Détails de l'analyse statistique
                        </h5>
                        <div className="space-y-3">
                          {selectedDataPoint.qualityReasons.map((reason, index) => (
                            <div key={index} className="flex items-start gap-3 text-sm">
                              <div className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-400 mt-2"></div>
                              <div className="text-gray-700 dark:text-gray-300">{reason}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommandations */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <h5 className="font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Recommandations
                      </h5>
                      <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                        {selectedDataPoint.qualityStatus === 'OK' ? (
                          <p>• Aucune action requise, la donnée semble fiable.</p>
                        ) : selectedDataPoint.qualityStatus === 'WARNING' ? (
                          <>
                            <p>• Vérifier avec le promoteur si la valeur est correcte</p>
                            <p>• Contrôler le contexte de saisie (unité, période, etc.)</p>
                            <p>• Comparer avec les données historiques du promoteur</p>
                          </>
                        ) : (
                          <>
                            <p>• Vérification urgente recommandée</p>
                            <p>• Contacter le promoteur pour confirmation</p>
                            <p>• Analyser les conditions de collecte des données</p>
                            <p>• Considérer l'exclusion temporaire de l'analyse</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCloseQualityModal}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GINumericQuestion;